import {
  Avatar,
  Badge,
  Cell,
  Column,
  Row,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { Link, useNavigate } from 'react-router';
import {
  invoiceLink,
  invoiceStatus,
  type ProviderInvoice,
} from '../../lib/invoices';
import { useProviderInvoices } from '../../hooks/use-provider-tabs';

const COLUMN_COUNT = 5;

function Customer({
  organization,
}: {
  organization: ProviderInvoice['customerOrganizationId'];
}) {
  if (!organization?.company_name) {
    return null;
  }
  return (
    <span className="sk:flex sk:items-center sk:gap-2">
      <Avatar
        src={organization.logo?.url}
        name={organization.company_name}
        size="xs"
      />
      {organization.company_name}
    </span>
  );
}

/** The WMS provider's invoices to its customers. */
export function InvoicesTab({ providerId }: { providerId: string }) {
  const navigate = useNavigate();
  const { data, loading, totalData, pageChanged, filter, filterChanged } =
    useProviderInvoices(providerId);
  const invoices = (data ?? []) as ProviderInvoice[];

  return (
    <TableFrame
      aria-label="Invoices"
      columnCount={COLUMN_COUNT}
      isLoading={loading}
      loadingRows={10}
      emptyMessage="No invoices yet"
      pagination={{
        page: filter.page,
        pageSize: filter.limit,
        total: invoices.length ? totalData : 0,
        onPageChange: pageChanged,
        onPageSizeChange: size => filterChanged('limit', size),
      }}
      header={
        <TableHeader>
          <Column id="number" isRowHeader className="sk:w-40">
            Invoice #
          </Column>
          <Column id="customer">Customer</Column>
          <Column id="status" className="sk:w-48">
            Status
          </Column>
          <Column id="created" className="sk:w-40">
            Created
          </Column>
          <Column id="total" className="sk:w-40">
            Total amount
          </Column>
        </TableHeader>
      }
    >
      {invoices.map(invoice => {
        const status = invoiceStatus(invoice.status);
        return (
          <Row
            key={invoice._id}
            id={invoice._id}
            onAction={() => navigate(invoiceLink(invoice._id, providerId))}
          >
            <Cell>
              <Link
                to={invoiceLink(invoice._id, providerId)}
                className="sk:font-medium sk:text-primary sk:no-underline sk:hover:underline"
              >
                {invoice.invoiceNumber}
              </Link>
            </Cell>
            <Cell>
              <Customer organization={invoice.customerOrganizationId} />
            </Cell>
            <Cell>
              {status.label ? (
                <Badge tone={status.tone}>{status.label}</Badge>
              ) : null}
            </Cell>
            <Cell>
              {DateUtility.formatDate(
                invoice.createdAt,
                DateFormat.barChartMonth,
              )}
            </Cell>
            <Cell className="sk:tabular-nums">
              {CommonUtility.currencyFormat(invoice.totalAmount, 'USD')}
            </Cell>
          </Row>
        );
      })}
    </TableFrame>
  );
}
